import { z } from 'zod/v4';

// please keep in sync with `rust-workspace/domain-core-extra-types/src/country_code.rs` on the BE
export const CountryAlpha2CodeRuntype = z.enum([
    'AF',
    'AX',
    'AL',
    'DZ',
    'AS',
    'AD',
    'AO',
    'AI',
    'AQ',
    'AG',
    'AR',
    'AM',
    'AW',
    'AU',
    'AT',
    'AZ',
    'BS',
    'BH',
    'BD',
    'BB',
    'BY',
    'BE',
    'BZ',
    'BJ',
    'BM',
    'BT',
    'BO',
    'BQ',
    'BA',
    'BW',
    'BV',
    'BR',
    'IO',
    'BN',
    'BG',
    'BF',
    'BI',
    'CV',
    'KH',
    'CM',
    'CA',
    'KY',
    'CF',
    'TD',
    'CL',
    'CN',
    'CX',
    'CC',
    'CO',
    'KM',
    'CG',
    'CD',
    'CK',
    'CR',
    'CI',
    'HR',
    'CU',
    'CW',
    'CY',
    'CZ',
    'DK',
    'DJ',
    'DM',
    'DO',
    'EC',
    'EG',
    'SV',
    'GQ',
    'ER',
    'EE',
    'ET',
    'FK',
    'FO',
    'FJ',
    'FI',
    'FR',
    'GF',
    'PF',
    'TF',
    'GA',
    'GM',
    'GE',
    'DE',
    'GH',
    'GI',
    'GR',
    'GL',
    'GD',
    'GP',
    'GU',
    'GT',
    'GG',
    'GN',
    'GW',
    'GY',
    'HT',
    'HM',
    'VA',
    'HN',
    'HK',
    'HU',
    'IS',
    'IN',
    'ID',
    'IR',
    'IQ',
    'IE',
    'IM',
    'IL',
    'IT',
    'JM',
    'JP',
    'JE',
    'JO',
    'KZ',
    'KE',
    'KI',
    'KP',
    'KR',
    'KW',
    'KG',
    'LA',
    'LV',
    'LB',
    'LS',
    'LR',
    'LY',
    'LI',
    'LT',
    'LU',
    'MO',
    'MK',
    'MG',
    'MW',
    'MY',
    'MV',
    'ML',
    'MT',
    'MH',
    'MQ',
    'MR',
    'MU',
    'YT',
    'MX',
    'FM',
    'MD',
    'MC',
    'MN',
    'ME',
    'MS',
    'MA',
    'MZ',
    'MM',
    'NA',
    'NR',
    'NP',
    'NL',
    'NC',
    'NZ',
    'NI',
    'NE',
    'NG',
    'NU',
    'NF',
    'MP',
    'NO',
    'OM',
    'PK',
    'PW',
    'PS',
    'PA',
    'PG',
    'PY',
    'PE',
    'PH',
    'PN',
    'PL',
    'PT',
    'PR',
    'QA',
    'RE',
    'RO',
    'RU',
    'RW',
    'BL',
    'SH',
    'KN',
    'LC',
    'MF',
    'PM',
    'VC',
    'WS',
    'SM',
    'ST',
    'SA',
    'SN',
    'RS',
    'SC',
    'SL',
    'SG',
    'SX',
    'SK',
    'SI',
    'SB',
    'SO',
    'ZA',
    'GS',
    'SS',
    'ES',
    'LK',
    'SD',
    'SR',
    'SJ',
    'SZ',
    'SE',
    'CH',
    'SY',
    'TW',
    'TJ',
    'TZ',
    'TH',
    'TL',
    'TG',
    'TK',
    'TO',
    'TT',
    'TN',
    'TR',
    'TM',
    'TC',
    'TV',
    'UG',
    'UA',
    'UK',
    'AE',
    'GB',
    'UM',
    'US',
    'UY',
    'UZ',
    'VU',
    'VE',
    'VN',
    'VG',
    'VI',
    'WF',
    'EH',
    'YE',
    'ZM',
    'ZW',
]);

export type CountryAlpha2Code = z.infer<typeof CountryAlpha2CodeRuntype>;

const AddressRuntype = z.object({
    streetAddress: z.string().nullable(),
    city: z.string().nullable(),
    country: CountryAlpha2CodeRuntype.nullable(),
    postcode: z.string().nullable(),
});

export type SiteType = z.infer<typeof SiteTypeRuntype>;
export const SiteTypeRuntype = z.enum(['Manufacturing', 'Inventory', 'Sourcing']);

export type SiteDTO = z.infer<typeof SiteRuntype>;
export const SiteRuntype = z.object({
    id: z.string(),
    name: z.string(),
    address: AddressRuntype.nullable(),
    notes: z.string().nullable(),
    site_number: z.string().nullable(),
    is_default_for: z.array(SiteTypeRuntype),
    tags: z.array(SiteTypeRuntype),
});

export type SitePostDTO = z.infer<typeof SitePostRuntype>;
export const SitePostRuntype = SiteRuntype.omit({ id: true });

export interface SiteLiteDTO extends z.infer<typeof SiteLiteDTORuntype> {}
export const SiteLiteDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    number: z.string().nullable(),
});

export const FindSitesMatchReasonRuntype = z.object({
    field: z.enum(['name', 'site_number']),
    /**
     * `close_match` is a "did you mean" suggestion the backend returns for a query
     * without any other match. Clients must never auto-accept those.
     */
    method: z.enum(['exact', 'alphanumeric', 'close_match']),
});
export type FindSitesMatchReasonDTO = z.infer<typeof FindSitesMatchReasonRuntype>;

export interface FindSitesMatchDTO extends z.infer<typeof FindSitesMatchRuntype> {}
export const FindSitesMatchRuntype = z.object({
    site: SiteLiteDTORuntype,
    match_reason: FindSitesMatchReasonRuntype,
});

export interface FindSitesResponseDTO extends z.infer<typeof FindSitesResponseRuntype> {}
export const FindSitesResponseRuntype = z.object({
    results: z.array(
        z.object({
            query: z.string(),
            matches: z.array(FindSitesMatchRuntype),
        }),
    ),
});
